<template>
  <vue-dropzone 
    ref="gk-file-uploader"
    id="gk-fileuploader-dropzone"
    :class="'dropzone-border'"
    :options="dropzoneOptions"
    :include-styling="true"
    :useCustomSlot="true"
    @vdropzone-error="fileError"
    @vdropzone-files-added="filesAdded"
    @vdropzone-removed-file="fileRemoved"
  >
    <div class="dropzone-custom-content">
      <font-awesome-icon icon="fa-solid fa-upload" class="dropzone-hint-icon" />
      <h3 class="dropzone-custom-title">{{ $gettext('Drag and drop to upload content') }}</h3>
      <div class="subtitle">{{ $gettext('...or click to select from your computer') }}</div>
    </div>
  </vue-dropzone>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import { niceBytes } from "@/utils/"

export default {
  name: 'FileUploader',
  components: {
    vueDropzone: vue2Dropzone
  },
  props: {
    showsize: {
      type: Boolean,
      default: false
    },
    fileSizeMax: { // in Mb, base 1000
      type: Number,
      default: null
    },
    maxFiles: { // null for unlimited
      type: Number,
      default: null
    }
  },
  data () {
    return {
      dropzoneOptions: {
        url: 'https://httpbin.org/post',
        headers: { '': '' },
        maxFilesize: ((this.fileSizeMax*1000*1000 / 1024) / 1024), // converted to base 1024
        addRemoveLinks: true,
        autoProcessQueue: false,
        disablePreviews: true,
        maxFiles: this.maxFiles
      },
    }
  },
  beforeDestroy () {
    // cleaning dropzone to prevent errors when destroying the component
    this.$refs['gk-file-uploader'].removeAllFiles()
  },
  methods: {
    fileError (file, message, xhr) {
      let alert_msg = message
      const file_size = niceBytes(file.size, 1000)
      const max_file_size = niceBytes(this.fileSizeMax*1000*1000, 1000, true)

      if (this.maxFiles && this.maxFiles === (this.getDropzoneFiles()).length) {
        alert_msg = this.$gettext('Cannot add more files. Max allowed files: %{ max }')
        alert_msg = this.$gettextInterpolate(alert_msg, { max: this.maxFiles })
      } else if (file_size > this.fileSizeMax) {
        alert_msg = this.$gettext('File is too big (%{ size }). Max filesize: %{ max }')
        alert_msg = this.$gettextInterpolate(alert_msg, { size: niceBytes(file.size, 1000, true), max: max_file_size })
      }

      this.$refs['gk-file-uploader'].removeFile(file)

      this.$store.commit('setAlert', {
        title: this.$gettext('Upload error'),
        text: alert_msg,
        variant: 'warning',
        duration: 4
      }, { root: true })
    },
    filesAdded (file) {
      this.updateQueuedFiles(file)
    },
    fileRemoved () {
      this.updateQueuedFiles()
    },
    updateQueuedFiles (file) {
      this.$nextTick(() => {
        this.$emit('updated-queued-files', false)
        this.$emit('fileUploaded', file)
      })
    },
    clear () {
      this.$refs['gk-file-uploader'].removeAllFiles()
    },
    getDropzoneFiles () {
      return this.$refs['gk-file-uploader'].getQueuedFiles()
    },
    getTotalFileSize () {
      const files = this.getDropzoneFiles()
      if (!Array.isArray(files)) return 0
      return files.reduce((total, file) => {
        return total + (file.size || 0)
      }, 0)
    }
  }
}
</script>

<style lang="scss">
  #gk-fileuploader-dropzone {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    &:hover {
      background: white;
    }
    .dropzone-custom-content {
      text-align: center;
      .dropzone-custom-title {
        color: $colorBrand;
      }
    }
    &.dropzone-border {
      border-radius: $radiusSm;
      border: 4px dashed rgba(55, 160, 205, 0.3) !important;
      border-image: none;
      width:100%;
      height: 100%;
    }
    .dz-message {
      margin: 0;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    &.dz-started { .dz-message { display: none; } }

    .dz-preview {
      display: flex;
      padding: 0.5rem;
      margin: 0;
      width: 100%;
      height: auto;
      min-height: auto;
      border-bottom: 1px solid $colorBorderDash;
      .dz-progress, .dz-image, .dz-error-message,
      .dz-success-mark, .dz-error-mark { display: none; }
      .dz-details, .dz-remove {
        padding: 0;
        position: relative;
        opacity: 1;
        font-size: 14px;
      }
      .dz-details { 
        background: none; 
        color: $colorTextBase; 
        display: flex;
        max-width: auto;
        min-width: auto;
        flex: 1 0 auto;
        .dz-size { margin: 0; }
        .dz-filename { 
          span { padding: 0.5rem; }
        }
      }
      .dz-remove { 
        top: auto; left: auto; bottom: auto; right: auto; padding: 0; margin: 0;
        color: $colorDanger; 
        border: 0;
        text-transform: none;
        font-weight: normal;
        letter-spacing: normal;
      }
    }
  }

</style>